import { useMemo, useRef } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useContainerWidth } from '@/hooks/use-container-width'
import { cn } from '@/lib/utils'

/** Middle-truncate long values so start and end stay readable (ASCII ellipsis). */
export function truncateMiddle(text: string, maxChars = 40): string {
  if (text.length <= maxChars) return text
  const budget = maxChars - 3
  if (budget < 4) return text.slice(0, Math.max(0, maxChars - 3)) + '...'
  const endKeep = Math.min(16, Math.floor(budget / 2))
  const startKeep = budget - endKeep
  return `${text.slice(0, startKeep)}...${text.slice(-endKeep)}`
}

/** Last path segment (filename); keeps leading slash-only paths intact. */
export function pathBasename(path: string): string {
  const trimmed = path.replace(/\/+$/, '')
  const parts = trimmed.split('/').filter((p) => p.length > 0)
  return parts[parts.length - 1] || path
}

/** Approx. chars that fit in a text-xs mono span (never from full-string min-width). */
function estimateMaxChars(widthPx: number | null, fallback = 12): number {
  if (widthPx == null || widthPx <= 0) return fallback
  return Math.max(6, Math.floor(widthPx / 7))
}

/**
 * Path display by available width:
 * - narrow: basename only
 * - medium: parent/basename (truncated if needed)
 * - wide: middle-truncated full path
 */
export function formatPathForWidth(path: string, widthPx: number | null): string {
  const maxChars = estimateMaxChars(widthPx)
  const base = pathBasename(path)

  if (widthPx == null || widthPx < 160) {
    return truncateMiddle(base, maxChars)
  }

  const parts = path.replace(/\/+$/, '').split('/').filter((p) => p.length > 0)
  if (widthPx < 260 && parts.length >= 2) {
    const short = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`
    return truncateMiddle(short, maxChars)
  }

  return truncateMiddle(path, maxChars)
}

export type DocumentInfoCopyChipProps = {
  /** Used for aria-label, toasts, and tooltip context (not shown as a visible text label). */
  label: string
  value: string
  /** Clipboard payload; defaults to `value`. */
  copyValue?: string
  icon?: LucideIcon
  successToast?: string
  /**
   * When `path`, narrow widths show basename only; wider show more path context.
   * Full `value` / `copyValue` still used for tooltip and clipboard.
   */
  displayMode?: 'plain' | 'path'
  /** Optional Cmd/Ctrl+click secondary action (e.g. reveal path in Browse). */
  onModifierClick?: () => void
  className?: string
  'data-testid'?: string
}

/**
 * Compact one-line Document Info control: icon + width-aware value + copy icon.
 * Entire control copies; never sizes itself from the full string.
 */
export function DocumentInfoCopyChip({
  label,
  value,
  copyValue,
  icon: Icon,
  successToast,
  displayMode = 'plain',
  onModifierClick,
  className,
  'data-testid': dataTestId,
}: DocumentInfoCopyChipProps) {
  const payload = copyValue ?? value
  const valueRef = useRef<HTMLSpanElement>(null)
  const valueWidth = useContainerWidth(valueRef)

  const display = useMemo(() => {
    if (displayMode === 'path') {
      return formatPathForWidth(value, valueWidth)
    }
    return truncateMiddle(value, estimateMaxChars(valueWidth, 10))
  }, [displayMode, value, valueWidth])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload)
      toast.success(successToast ?? `${label} copied`, {
        description: payload,
        duration: 2000,
      })
    } catch (err) {
      console.error(`Failed to copy ${label}:`, err)
      toast.error(`Failed to copy ${label}`)
    }
  }

  return (
    <button
      type="button"
      data-testid={dataTestId}
      aria-label={`${label}: ${payload}. Click to copy`}
      onClick={(e) => {
        if (onModifierClick && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          onModifierClick()
          return
        }
        void handleCopy()
      }}
      title={
        onModifierClick
          ? `${payload}\nClick to copy · Cmd/Ctrl+click for Browse`
          : `${payload}\nClick to copy`
      }
      className={cn(
        'flex w-full min-w-0 max-w-full items-center gap-1.5 h-7 px-1.5 rounded-md',
        'text-left text-xs bg-muted/60 hover:bg-muted transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        'overflow-hidden',
        className
      )}
    >
      {Icon ? (
        <Icon className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
      ) : null}
      <span
        ref={valueRef}
        className="min-w-0 flex-1 overflow-hidden whitespace-nowrap font-mono text-foreground/90"
      >
        {display}
      </span>
      <Copy className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  )
}
